import {LinesChangedCounterLabel} from '@github-ui/diff-file-header'
import {DiffViewSettings} from '@github-ui/diff-view-settings'
import type {CommitNotices, UserNotice} from '@github-ui/diff-view-settings/types'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {useStickyObserver} from '@github-ui/use-sticky-header/useStickyObserver'
import {ArrowUpIcon} from '@primer/octicons-react'
import {Button, Heading} from '@primer/react'
import {memo} from 'react'

import type {HeaderInfo} from '../../types/commit-types'

interface DiffsHeaderProps {
  headerInfo: HeaderInfo
  treeToggleElement: React.ReactElement
  searchBox?: React.ReactElement
  ignoreWhitespace?: boolean
  userNotices?: Array<UserNotice<CommitNotices>>
}

export const DiffsHeader = memo(DiffsHeaderUnmemoized)

function DiffsHeaderUnmemoized({
  treeToggleElement,
  headerInfo,
  searchBox,
  ignoreWhitespace,
  userNotices,
}: DiffsHeaderProps) {
  const {isSticky: isStickied, sentinelRef: diffHeaderRef} = useStickyObserver()
  const hideLinesChangedClass = isStickied ? 'd-none d-sm-flex' : '' // make room for the Top button on mobile
  /* eslint-disable-next-line @github-ui/github-monorepo/no-use-feature-flags --
     TODO: Migrate to isFeatureEnabled from @github-ui/feature-flags. See
     https://thehub.github.com/epd/engineering/dev-practicals/frontend/react/data-router/migration/app-payload-deprecation/ */
  const robustSmallScreenSupport = useFeatureFlag('prx_robust_small_screen_support')

  return (
    <div
      className={`d-flex flex-items-center ${!robustSmallScreenSupport ? 'flex-justify-between' : ''} gap-2 tmp-pt-3 tmp-pt-lg-4 pb-2 position-sticky top-0 color-bg-default`}
      style={{zIndex: 2}}
      ref={diffHeaderRef}
    >
      <div className="d-flex flex-items-center">
        {treeToggleElement}
        {!robustSmallScreenSupport && (
          <>
            <Heading as="h2" className="mx-2 f4">
              {headerInfo.filesChangedString} file{headerInfo.filesChanged > 1 ? 's' : ''} changed
            </Heading>

            <LinesChangedCounterLabel className={hideLinesChangedClass} isAddition>
              +{headerInfo.additions}
            </LinesChangedCounterLabel>
            <LinesChangedCounterLabel className={hideLinesChangedClass} isAddition={false}>
              -{headerInfo.deletions}
            </LinesChangedCounterLabel>
            <span className={`${hideLinesChangedClass} ml-2 fgColor-muted text-small`} style={{whiteSpace: 'nowrap'}}>
              lines changed
            </span>
          </>
        )}
      </div>

      <div className={`d-flex gap-2 flex-items-center ${robustSmallScreenSupport ? 'width-full' : ''}`}>
        {isStickied && <GoToTopButton />}
        {searchBox !== undefined && searchBox}
        <DiffViewSettings reloadOnWhitespaceChange defaultHideWhitespace={ignoreWhitespace} userNotices={userNotices} />
      </div>
    </div>
  )
}

const GoToTopButton = () => {
  return (
    <Button
      leadingVisual={ArrowUpIcon}
      variant="invisible"
      className="fgColor-default px-2 f6 gap-1"
      onClick={event => {
        event.preventDefault()
        window.scrollTo({top: 0, behavior: 'smooth'})
      }}
    >
      Top
    </Button>
  )
}
